import { useEffect, useRef, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { copy } from '@/copy'
import { Mark } from '@/components/Mark'
import { useConsole } from '@/lib/store'

// The frame of every signed-in view: a skip link, the glass top bar with the
// wordmark, the three views and the account, a line when Purlview cannot be
// reached, and the polite live region every status change is spoken in.
export function Shell({
  account,
  counts,
  offline,
  onSignOut,
  signingOut,
  children,
}: {
  account: string
  counts: { shares?: number; devices?: number }
  offline: boolean
  onSignOut: () => void
  signingOut: boolean
  children: ReactNode
}) {
  return (
    <div className="page">
      <a className="skip" href="#main">
        {copy.nav.skip}
      </a>
      <header className="glass sticky top-0 z-20 border-b border-rule">
        <div className="mx-auto flex min-h-[64px] max-w-page items-center gap-3 px-4 min-[481px]:gap-6 min-[481px]:px-6">
          <Link to="/" className="inline-flex min-h-[44px] min-w-[44px] items-center gap-2.5 text-[20px] font-medium tracking-[-0.035em] text-text no-underline">
            <Mark />
            <span className="max-[430px]:visually-hidden">{copy.wordmark}</span>
          </Link>
          <nav aria-label={copy.nav.label} className="flex min-w-0 items-center gap-0.5">
            <NavLink to="/" label={copy.nav.shares} count={counts.shares} />
            <NavLink to="/devices" label={copy.nav.devices} count={counts.devices} />
            <NavLink to="/account" label={copy.nav.account} />
          </nav>
          <div className="ml-auto hidden items-center gap-4 min-[900px]:flex">
            <span className="text-secondary [overflow-wrap:anywhere]">{account}</span>
            <button type="button" className="action min-h-[36px] border-quiet px-3 py-1.5 text-[14px]" onClick={onSignOut} aria-disabled={signingOut || undefined}>
              {signingOut ? copy.signingOut : copy.signOut}
            </button>
          </div>
        </div>
      </header>
      {offline && (
        <p className="m-0 border-b border-rule bg-sunken px-4 py-2.5 text-center text-[15px] leading-[22px]" role="status">
          <span className="text-attention" aria-hidden="true">
            !{' '}
          </span>
          {copy.offline}
        </p>
      )}
      <main id="main" tabIndex={-1} className="dense mx-auto max-w-page px-4 pb-16 outline-none min-[481px]:px-6">
        {children}
      </main>
      <LiveRegion />
    </div>
  )
}

function NavLink({ to, label, count }: { to: '/' | '/devices' | '/account'; label: string; count?: number }) {
  return (
    <Link
      to={to}
      activeOptions={{ exact: true, includeSearch: false }}
      className="group inline-flex min-h-[44px] items-center gap-2 rounded-control px-2.5 py-2.5 text-[15px] text-secondary no-underline transition-colors hover:text-text min-[481px]:px-3 [&[aria-current=page]]:font-medium [&[aria-current=page]]:text-text"
    >
      <span className="group-aria-[current=page]:underline group-aria-[current=page]:decoration-thread group-aria-[current=page]:decoration-1 group-aria-[current=page]:underline-offset-8">{label}</span>
      {count !== undefined && (
        <span className="hidden min-w-[22px] rounded-[4px] border border-rule px-[6px] text-center font-mono text-[12px] font-normal leading-5 text-secondary no-underline min-[560px]:inline-block" aria-hidden="true">
          {count}
        </span>
      )}
    </Link>
  )
}

function LiveRegion() {
  const { text, at } = useConsole((s) => s.announcement)
  const el = useRef<HTMLParagraphElement>(null)
  useEffect(() => {
    if (!el.current) return
    el.current.textContent = ''
    const t = setTimeout(() => el.current && (el.current.textContent = text), 50)
    return () => clearTimeout(t)
  }, [text, at])
  return <p ref={el} className="visually-hidden" role="status" aria-live="polite" />
}

/** The head of a view: its title with its count beside it, its one line if it needs one, and its action. */
export function PageHead({ label, title, lead, action }: { label?: string; title: string; lead?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3 pb-5 pt-7 min-[481px]:pt-9">
      <div className="min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h1 className="page-title">{title}</h1>
          {label && <p className="m-0 text-[15px] leading-[22px] text-secondary">{label}</p>}
        </div>
        {lead && <p className="m-0 mt-1 max-w-[40em] text-[15px] leading-[22px] text-secondary">{lead}</p>}
      </div>
      {action}
    </div>
  )
}
